import React, { useState, useMemo } from "react";
import { Search, X } from "lucide-react";
import { GameCard } from "@/components/GameCard";
import { AdBanner } from "@/components/AdBanner";
import Seo from "@/components/Seo";
import { GAMES, QUIZZES } from "@/data/games";

const ALL = [...GAMES, ...QUIZZES];

const FILTERS = [
  { key: "all", label: "All", match: () => true },
  { key: "arcade", label: "Arcade", match: (g) => g.category === "arcade" && g.type !== "versus" },
  { key: "brain", label: "Brain & IQ", match: (g) => g.category === "brain" },
  { key: "quiz", label: "Quizzes", match: (g) => g.type === "quiz" },
  { key: "versus", label: "2-Player", match: (g) => g.type === "versus" },
];

export default function AllGames() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    const q = query.trim().toLowerCase();
    return ALL.filter((g) => f.match(g) && (!q || g.title.toLowerCase().includes(q) || g.blurb.toLowerCase().includes(q)));
  }, [query, filter]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="all-games-page">
      <Seo title="All Games & Quizzes — Browse the Full Arcade" description="Browse all our free games and quizzes on Video Game Mania. Search arcade games, brain teasers, IQ tests, 2-player games and personality quizzes." />

      <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tighter">Browse Everything</h1>
      <p className="text-zinc-400 mt-2 mb-8">
        <span className="text-neon-cyan font-bold">{GAMES.length}</span> games ·{" "}
        <span className="text-neon-pink font-bold">{QUIZZES.length}</span> quizzes · all free to play
      </p>

      <div className="flex flex-col md:flex-row gap-4 md:items-center mb-8">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="games-search"
            placeholder="Search games & quizzes…"
            className="w-full bg-zinc-950 border-2 border-zinc-700 focus:border-neon-cyan outline-none rounded-lg pl-10 pr-9 py-2.5 text-white" />
          {query && (
            <button onClick={() => setQuery("")} data-testid="games-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`games-filter-${f.key}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-full border-2 transition-colors ${
                filter === f.key ? "bg-neon-cyan text-black border-neon-cyan" : "bg-transparent text-zinc-300 border-zinc-700 hover:border-white"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="text-zinc-500 text-center py-20 font-mono" data-testid="games-empty">No matches for "{query}". Try another search.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {shown.map((g, i) => <GameCard key={g.id} item={g} index={i} />)}
        </div>
      )}

      <AdBanner slot="all-games" className="mt-12" />
    </div>
  );
}
